import * as helpers from './helpers';

export type SchemaReturnType =
  | string
  | boolean
  | number
  | string[]
  | number[]
  | Function
  | null
  | object;

export type SchemaRuleType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  | 'function'
  | 'null'
  | 'undefined';

export type SchemaRule = {
  types?: SchemaRuleType[];
  default?: SchemaReturnType;
  oneOf?: string[];
  isOptional?: boolean;
};

export type ValidationCallback<R = Record<string, SchemaRule>> = {
  key: keyof R;
  entry: SchemaReturnType;
  isOptional: boolean | undefined;
};

type InferReturnTypeFromTypes<F> = F extends 'string'
  ? string
  : F extends 'number'
  ? number
  : F extends 'boolean'
  ? boolean
  : F extends 'array'
  ? string[]
  : F extends 'object'
  ? object | null
  : F extends 'function'
  ? Function
  : F extends 'undefined'
  ? undefined
  : never;

type SchemaSubject<Rules extends Record<string, SchemaRule>> = {
  [K in keyof Rules]: Rules[K]['types'] extends SchemaRuleType[]
    ? // This allows us to get the actual types of the value from the types array
      // And also include an optional type that comes from the `default` value when present
      InferReturnTypeFromTypes<Rules[K]['types'][number]>
    : never;
};

export const create = <R extends Record<string, SchemaRule>>(
  name: string,
  createError: (error: string) => Error,
  rules: R,
  omitExtras = true
) => {
  const storedRules = helpers.shallowCopy(rules);

  const getKeys = () => helpers.getObjectKeys(storedRules);

  const mutate = (callback: (schema: R) => R) => {
    const updatedRules = callback(
      helpers.mapObject(storedRules, (key) =>
        helpers.shallowCopy(storedRules[key])
      )
    );

    return create(name, createError, updatedRules, omitExtras);
  };

  const validate = (
    subject: SchemaSubject<R>,
    caller: string,
    callback?: (props: ValidationCallback<R>) => void
  ) => {
    getKeys().forEach((key) => {
      const { types, oneOf, isOptional } = storedRules[key];
      const entry: SchemaReturnType | undefined = subject[key];
      const isDefined = entry !== undefined;
      const typeOfValue = helpers.getRealTypeOf(entry);

      if (!isOptional && !isDefined) {
        throw createError(
          `Missing required ${name} "${key}". ${caller} requires that a value be passed for ${name} "${key}". Received "undefined".`
        );
      }

      if (!isDefined) {
        return;
      }

      if (helpers.isArray(types) && !types.includes(typeOfValue)) {
        throw createError(
          `Invalid ${name}. ${caller} requires that the "${key}" ${name} type be one of ["${types!.join(
            '", "'
          )}"]. Received "${typeOfValue}".`
        );
      }

      if (typeOfValue === 'function') {
        return;
      }

      if (helpers.isArray(oneOf) && !oneOf.includes(String(entry))) {
        throw createError(
          `Invalid ${name}. ${caller} requires that the "${key}" ${name} be one of ["${oneOf!.join(
            '", "'
          )}"]. Received "${entry}".`
        );
      }

      helpers.ensureFn(callback)({ key, entry, isOptional });
    });

    return true;
  };

  const validateEach = (
    subject: Record<string, SchemaSubject<R>>,
    caller: string
  ) => {
    helpers.getObjectKeys(subject).forEach((key) => {
      const entry = subject[key];
      validate(entry, caller);
    });
  };

  const normalize = <Subject extends SchemaSubject<R>>(
    subject: Partial<Subject>
  ) => {
    const pluckedSubject = helpers.pluck<SchemaRule, R>('default', storedRules);
    const withDefaults = helpers.defaults(subject, pluckedSubject) as Subject;

    if (omitExtras) {
      return helpers.pick(withDefaults, getKeys()) as Subject;
    }

    return withDefaults;
  };

  const normalizeEach = <Subject extends SchemaSubject<R>>(
    subject: Record<string, Partial<Subject>>
  ) => {
    return helpers.reduceObject(subject)((accumulator, key) => {
      const entry = subject[key];
      accumulator[key] = normalize(entry);
      return accumulator;
    }) as Record<string, Subject>;
  };

  return {
    getKeys,
    mutate,
    normalize,
    normalizeEach,
    validate,
    validateEach,
    _peek: () => helpers.deepFreeze(storedRules),
  };
};
